import axios from "axios";
import { PDA_PREFIX, SCENARIO_GANTT_ROOT_URL } from "../../global/constants";
import {
  SELECT_REGULARS,
  SELECT_TASKS,
  UPDATE_PANELS_HEIGHT,
  UPDATE_GANTT_PERIOD,
  UPDATE_GANTT_SIZE,
  RESIZE_PIVOT_CHART,
  UPDATE_SELECTED_TAB_INDEX,
  SET_UPDATING_STATUS,
  SELECT_ROW,
  ALLOW_TO_RENDER,
  DISABLE_SAVING,
  SET_TASK_TO_ASSIGN,
  SET_ITEM_TO_EDIT,
  SET_MY_SCOPES_VIEW,
  SET_WORKLOAD_FILTERS,
  SET_INTER_SCOPE_FILTERS,
  ADD_ITEM_PENDING,
  REMOVE_ITEM_PENDING,
  CLEAR_ITEMS_PENDING,
  SET_SELECTED_ACTIVITY,
  SET_REGULAR_TO_ASSIGN,
  SET_CHART_FILTERING,
  SET_WORK_DAYS_CALENDAR,
  RECREATE_SCHEDULER_INSTANCE,
  SET_GANTT_CUSTOM_COLUMNS,
  RESET_GANTT_CUSTOM_COLUMNS,
  SET_USE_CUSTOM_MULTI_PROJECT_GANTT,
  SET_MULTI_PROJECT_EDITING_ENABLED,
} from "../types";
import { preferenceStorage } from "../user-preferences/preferenceStorage";

export const USE_CUSTOM_MULTI_PROJECT_GANTT_LS_KEY =
  "useCustomMultiProjectGantt-V1";

/**
 * Toggle between the existing Bryntum-based multiproject Gantt view and the
 * new from-scratch React + SVG implementation. Persisted to localStorage so
 * the preference survives reloads.
 */
export const setUseCustomMultiProjectGantt = (value) => (dispatch) => {
  preferenceStorage("timeline_view").set(USE_CUSTOM_MULTI_PROJECT_GANTT_LS_KEY, !!value);
  dispatch({
    type: SET_USE_CUSTOM_MULTI_PROJECT_GANTT,
    payload: !!value,
  });
};

export const initUseCustomMultiProjectGanttFromStorage = () => (dispatch) => {
  const stored = preferenceStorage("timeline_view").get(USE_CUSTOM_MULTI_PROJECT_GANTT_LS_KEY, false);
  dispatch({
    type: SET_USE_CUSTOM_MULTI_PROJECT_GANTT,
    payload: !!stored,
  });
};

/**
 * Is the Generative Workspace on for this portfolio? A PORTFOLIO option
 * (portfolio.details.generativeWorkspaceEnabled), not a per-user toggle. Used by
 * navigation sites (ScenariosGrid, scenario-creation thunks) that need to route to
 * /generative-scheduling-experimental without subscribing to the store; inside a
 * component prefer useSelector(state.portfolio.details?.generativeWorkspaceEnabled)
 * so it re-renders when the details land.
 */
export const isGenerativeSchedulingExperimentalEnabled = () => {
  // Required lazily: this module is imported by the store's own reducer graph,
  // so a top-level import of the store would close the cycle at load time.
  const store = require("../../store/store").default;
  return store.getState().portfolio?.details?.generativeWorkspaceEnabled === true;
};

/**
 * Route guard flavour of the check. On a direct hit of the workspace URL the
 * portfolio details are still in flight, and answering `false` there would
 * bounce the user to the dashboard before the option is even known — so report
 * "pending" (null) until the details land, which PermissionGuard renders as a
 * loader.
 */
export const generativeWorkspaceRoutePermission = () => {
  const store = require("../../store/store").default;
  const details = store.getState().portfolio?.details;
  if (!details || Object.keys(details).length === 0) return null;
  return details.generativeWorkspaceEnabled === true;
};

export const buildGenerativeSchedulingExperimentalPath = ({
  projectId,
  scenarioId,
  structureId,
}) => {
  const params = new URLSearchParams();
  if (projectId != null) params.set("projectId", String(projectId));
  if (scenarioId != null) params.set("scenarioId", String(scenarioId));
  if (structureId != null) params.set("structureId", String(structureId));
  const qs = params.toString();
  return qs
    ? `/generative-scheduling-experimental?${qs}`
    : "/generative-scheduling-experimental";
};

/**
 * Destination for a freshly created project/scenario, given the
 * `projectId/scenarioId/structureId` path the quick-project endpoint returns.
 * Follows the portfolio's Generative Workspace option, falling back to the
 * classic Gantt.
 */
export const buildCreatedScenarioPath = (path) => {
  const [projectId, scenarioId, structureId] = String(path).split("/");
  return isGenerativeSchedulingExperimentalEnabled()
    ? buildGenerativeSchedulingExperimentalPath({
        projectId,
        scenarioId,
        structureId,
      })
    : `/${SCENARIO_GANTT_ROOT_URL}/${path}`;
};

export const MULTI_PROJECT_EDITING_LS_KEY = "multiProjectEditingEnabled-V1";

/**
 * Per-user PREFERENCE, off by default: shows the multi-project Gantt's editing
 * affordances. It sits at the bottom of the permission precedence — the server
 * still filters and authorises per project (capabilities in the payload,
 * assertCanModifySAs on every write), so this governs what the UI offers,
 * never what the backend will accept.
 */
export const setMultiProjectEditingEnabled = (value) => (dispatch) => {
  preferenceStorage("multi_project_editing").set(MULTI_PROJECT_EDITING_LS_KEY, !!value);
  dispatch({ type: SET_MULTI_PROJECT_EDITING_ENABLED, payload: !!value });
};

/** Replays the server-side preference into the store once the login load landed (SPEC-0061). */
export const restoreMultiProjectEditingPreference = () => (dispatch) => {
  dispatch({
    type: SET_MULTI_PROJECT_EDITING_ENABLED,
    payload: preferenceStorage("multi_project_editing").get(MULTI_PROJECT_EDITING_LS_KEY, false) === true,
  });
};

export const selectRegulars = (regulars) => ({
  type: SELECT_REGULARS,
  payload: regulars,
});

export const selectTasks = (tasks) => ({
  type: SELECT_TASKS,
  payload: tasks,
});

export const updatePanelsHeight = (data) => ({
  type: UPDATE_PANELS_HEIGHT,
  payload: data,
});

export const updateGanttPeriod = (period) => ({
  type: UPDATE_GANTT_PERIOD,
  payload: period,
});

export const updateGanttSize = (data) => ({
  type: UPDATE_GANTT_SIZE,
  payload: data,
});

export const resizePivotChart = (isResizing) => ({
  type: RESIZE_PIVOT_CHART,
  payload: isResizing,
});

export const updateSelectedTabIndex = (id, value) => ({
  type: UPDATE_SELECTED_TAB_INDEX,
  payload: { id, value },
});

export const selectRow = (id) => ({
  type: SELECT_ROW,
  payload: id,
});

export const setUpdatingStatus = (status) => ({
  type: SET_UPDATING_STATUS,
  payload: status,
});

export const allowToRender = (allow) => ({
  type: ALLOW_TO_RENDER,
  payload: allow,
});

export const changeDisableSavingStatus = (status) => ({
  type: DISABLE_SAVING,
  payload: status,
});

export const setTaskToAssign = (taskData) => ({
  type: SET_TASK_TO_ASSIGN,
  payload: taskData,
});

export const setItemToEdit = (id) => ({
  type: SET_ITEM_TO_EDIT,
  payload: id,
});

export const setMyScopesView = (view) => ({
  type: SET_MY_SCOPES_VIEW,
  payload: view,
});

export const setWorkloadFilters = (filters) => ({
  type: SET_WORKLOAD_FILTERS,
  payload: filters,
});

export const setInterScopeFilters = (filters) => ({
  type: SET_INTER_SCOPE_FILTERS,
  payload: filters,
});

export const addItemPending = (id) => ({
  type: ADD_ITEM_PENDING,
  payload: id,
});

export const removeItemPending = (id) => ({
  type: REMOVE_ITEM_PENDING,
  payload: id,
});

export const clearItemsPending = () => ({
  type: CLEAR_ITEMS_PENDING,
});

export const setSelectedActivity = (activity) => ({
  type: SET_SELECTED_ACTIVITY,
  payload: activity,
});

export const setRegularToAssign = (regular) => ({
  type: SET_REGULAR_TO_ASSIGN,
  payload: regular,
});

export const setChartFiltering = (data) => ({
  type: SET_CHART_FILTERING,
  payload: data,
});

export const setWorkDaysCalendar = (calendar) => ({
  type: SET_WORK_DAYS_CALENDAR,
  payload: calendar,
});

export const recreateSchedulerInstance = (payload) => ({
  type: RECREATE_SCHEDULER_INSTANCE,
  payload,
});

export const fetchGanttCustomColumns = () => async (dispatch) => {
  dispatch({ type: RESET_GANTT_CUSTOM_COLUMNS });
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/schedule-activity/custom-column/all-names`
    );
    dispatch({ type: SET_GANTT_CUSTOM_COLUMNS, payload: res.data });
  } catch (err) {}
};
